import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import defaultTheme from '../Config/defaultTheme';

/**
 * Custom hook to access theme settings from Redux store
 * Provides easy access to theme colors, fonts, spacing, layout, and effects
 * Falls back to default theme values when theme is not loaded
 * 
 * @returns {Object} Theme object with colors, font, spacing, layout, effects
 * 
 * @example
 * // In a component
 * const { colors, font, spacing } = useTheme();
 * 
 * // Access specific values
 * const primaryColor = colors.primary;
 * const fontSize = font.sizeBase;
 */
const useTheme = () => {
    const themeSettings = useSelector((state) => state.mainReducerData.themeSettings);
    
    const theme = useMemo(() => {
        return themeSettings || defaultTheme;
    }, [themeSettings]);
    
    return theme;
};

/**
 * Hook to get a specific color from the theme
 * @param {string} colorKey - Key of the color (e.g., 'primary', 'secondary')
 * @returns {string} Color value
 * 
 * @example
 * const primaryColor = useThemeColor('primary');
 */
export const useThemeColor = (colorKey) => {
    const theme = useTheme();
    return theme.colors?.[colorKey] || defaultTheme.colors[colorKey];
};

/**
 * Hook to get multiple colors from the theme
 * @param {string[]} colorKeys - Array of color keys
 * @returns {Object} Object with requested colors
 * 
 * @example
 * const { primary, secondary } = useThemeColors(['primary', 'secondary']);
 */
export const useThemeColors = (colorKeys) => {
    const theme = useTheme();
    return useMemo(() => {
        const colors = {};
        colorKeys.forEach((key) => {
            colors[key] = theme.colors?.[key] || defaultTheme.colors[key];
        });
        return colors;
    }, [theme, colorKeys]);
};

/**
 * Hook to check if theme is loaded from API
 * @returns {boolean} True if theme is loaded
 */
export const useThemeLoaded = () => {
    const themeSettings = useSelector((state) => state.mainReducerData.themeSettings);
    return themeSettings !== null;
};

/**
 * Hook to get CSS variable value for inline styles
 * @param {string} variableName - CSS variable name without 'var()' wrapper
 * @returns {string} CSS var() string ready for use in style props
 * 
 * @example
 * const primaryVar = useCssVar('--color-primary');
 * // Returns: 'var(--color-primary)'
 */
export const useCssVar = (variableName) => {
    return `var(${variableName})`;
};

/**
 * Utility to get inline style object with theme colors
 * Useful for components that need dynamic inline styles
 * 
 * @example
 * const theme = useTheme();
 * const buttonStyle = getThemedStyle(theme, {
 *   backgroundColor: 'primary',
 *   color: 'buttonPrimaryText'
 * });
 */
export const getThemedStyle = (theme, styleMap) => {
    const style = {};
    Object.entries(styleMap).forEach(([cssProperty, themeKey]) => {
        if (theme.colors?.[themeKey]) {
            style[cssProperty] = theme.colors[themeKey];
        } else if (theme.font?.[themeKey]) {
            style[cssProperty] = typeof theme.font[themeKey] === 'number' 
                ? `${theme.font[themeKey]}px` 
                : theme.font[themeKey];
        } else if (theme.spacing?.[themeKey]) {
            style[cssProperty] = typeof theme.spacing[themeKey] === 'number' 
                ? `${theme.spacing[themeKey]}px` 
                : theme.spacing[themeKey];
        }
    });
    return style;
};

export default useTheme;
